import React, { useState } from 'react';
import PageLayout from '../../../components/PageLayout/CarelonRx';
import { ContactDetails } from '../../../types/mfa';
import CancelMFAModal from '../../../components/modals/CancelMFAModal';
import ReviewDropdown from '../../../components/ReviewDropdown/ReviewDropdown';
import { useFlow } from '../../../contexts/FlowContext';
import '../../../styles/CarelonRx/login.css';
import '../../../styles/CarelonRx/mfa.css';

interface ReviewDetailsPageProps {
  error?: string;
  contactDetails?: ContactDetails | null;
  supportNumber?: string;
  supportNumberError?: string;
  onRetry?: () => void;
  onConfirm?: (selectedPhoneId: string, selectedEmailId: string) => void;
  onBack: () => void;
  previousStep?: () => void;
}

const ReviewDetailsPage: React.FC<ReviewDetailsPageProps> = ({
  error,
  contactDetails,
  supportNumber,
  supportNumberError,
  onRetry,
  onConfirm,
  onBack,
  previousStep,
}) => {
  const { getFlowData } = useFlow();
  const isNativeApp = getFlowData('device') === 'native_app';
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>(
    contactDetails?.phones?.[0]?.id || ''
  );
  const [selectedEmailId, setSelectedEmailId] = useState<string>(
    contactDetails?.emails?.[0]?.id || ''
  );
  const [hasSelectedPhone, setHasSelectedPhone] = useState<boolean>(false);
  const [hasSelectedEmail, setHasSelectedEmail] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState<boolean>(false);
  const [isEmailDropdownOpen, setIsEmailDropdownOpen] = useState<boolean>(false);

  // Determine if we have single or multiple contacts
  const hasMultiplePhones = (contactDetails?.phones?.length || 0) > 1;
  const hasMultipleEmails = (contactDetails?.emails?.length || 0) > 1;
  const hasSinglePhone = (contactDetails?.phones?.length || 0) === 1;
  const hasSingleEmail = (contactDetails?.emails?.length || 0) === 1;
  const hasPhone = (contactDetails?.phones?.length || 0) > 0;
  const hasEmail = (contactDetails?.emails?.length || 0) > 0;
  const hasNoContacts = !hasPhone && !hasEmail && !error && contactDetails;

  const handleConfirm = () => {
    const phoneId = selectedPhoneId || contactDetails?.phones?.[0]?.id || '';
    const emailId = selectedEmailId || contactDetails?.emails?.[0]?.id || '';
    
    if (onConfirm) {
      onConfirm(phoneId, emailId);
    }
  };

  return (
    <PageLayout bannerTitle="Review Details">
      <div className="login-page-container">
        <div className={`login-left-panel ${error ? 'api-error-layout' : ''} ${hasNoContacts ? 'no-contacts-layout' : ''}`}>
          {/* Back Button */}
          {!isNativeApp && (
            <a className="back-link mfa-back-link" onClick={hasNoContacts && previousStep ? previousStep : onBack}>
              <img src="/assets/carelonRx/images/Keyboard Arrow Left.svg" alt="" />
              <span>{hasNoContacts ? 'Back' : 'Back to Log In'}</span>
            </a>
          )}

          {/* SOA API Error State */}
          {error && (
            <>
              {/* Error Box with Retry Button */}
              <div className="mfa-soa-error-container">
                <div className="mfa-soa-error-content">
                  <img
                    src="/assets/carelonRx/images/infoIconRed.svg"
                    alt="Error"
                    className="mfa-soa-error-icon"
                  />
                  <div className="mfa-soa-error-text">
                    Sorry, we are experiencing technical issues. Please give us time to fix the
                    issues. Then, try again.
                  </div>
                </div>
                <div className="mfa-retry-wrapper">
                  <button onClick={onRetry} className="mfa-retry-button">
                    Retry
                  </button>
                </div>
              </div>

              {/* Technical Support Disclaimer for Contact API Error */}
              <div className="mfa-footer">
                <div className="disclaimer-section">
                  <div className="disclaimers">
                    <img
                      src="/assets/carelonRx/images/info.svg"
                      alt=""
                      className="mfa-info-icon"
                    />
                    <div className="mfa-disclaimer-text">
                      If we don't have the correct contact information to send your verification code, please call our Technical Support team at <a href="tel:1-844-430-0335">1-844-430-0335</a> (TTY: 711). Support is available Monday - Friday, 8 a.m. to 8 p.m. ET.
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Multiple Contacts Selection State */}
          {!error && contactDetails && (
            <>
              {/* Title */}
              <h1 className="mfa-title">To keep your account safe, please take a minute to review these details.</h1>

              {/* Subtitle */}
              <p className="mfa-subtitle">
                This is the contact information to be used to recover your account if you forget your username and/or password.
              </p>

              {/* Contact Selection - Styled Dropdown Fields */}
              <div className={`review-contact-container ${hasPhone && hasEmail ? 'two-fields' : (hasMultiplePhones || hasMultipleEmails) ? 'single-field' : ''}`}>
                <div className="review-field-group">
                  {/* Phone Field - Dropdown for multiple, Disabled for single */}
                  {hasPhone && (
                    <div className={`review-input-field ${isPhoneDropdownOpen ? 'dropdown-open' : ''}`}>
                      <label className="review-field-label">
                        {hasMultiplePhones ? 'Choose Phone Number' : 'Phone Number'}
                      </label>
                      {hasMultiplePhones ? (
                        <ReviewDropdown
                          options={contactDetails.phones.map(phone => ({
                            id: phone.id,
                            label: phone.maskedNumber
                          }))}
                          value={selectedPhoneId}
                          onChange={setSelectedPhoneId}
                          placeholder="Choose Phone Number"
                          onOpenChange={setIsPhoneDropdownOpen}
                        />
                      ) : (
                        <input 
                          className="crx-input" 
                          disabled 
                          value={contactDetails.phones[0].maskedNumber}
                          readOnly
                        />
                      )}
                    </div>
                  )}

                  {/* Email Field - Dropdown for multiple, Disabled for single */}
                  {hasEmail && (
                    <div className={`review-input-field ${isEmailDropdownOpen ? 'dropdown-open' : ''}`}>
                      <label className="review-field-label">
                        {hasMultipleEmails ? 'Choose Email Address' : 'Email Address'}
                      </label>
                      {hasMultipleEmails ? (
                        <ReviewDropdown
                          options={contactDetails.emails.map(email => ({
                            id: email.id,
                            label: email.maskedEmail
                          }))}
                          value={selectedEmailId}
                          onChange={setSelectedEmailId}
                          placeholder="Choose Email Address"
                          onOpenChange={setIsEmailDropdownOpen}
                        />
                      ) : (
                        <input 
                          className="crx-input" 
                          disabled 
                          value={contactDetails.emails[0].maskedEmail}
                          readOnly
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Support Disclaimer Below Fields */}
              <div className="disclaimer-section">
                {!supportNumberError ? (
                  <div className="disclaimers">
                    <img
                      src="/assets/carelonRx/images/info.svg"
                      alt=""
                      className="mfa-info-icon"
                    />
                    <div className="mfa-disclaimer-text">
                      If we don't have the correct contact details to send your verification code, please contact Pharmacy Member Services <a href={`tel:${supportNumber}`} className="mfa-contact-phone-link">{supportNumber}</a> (TTY: 711) for assistance 24 hours a day, 7 days a week.
                    </div>
                  </div>
                ) : (
                  <div className="disclaimers">
                    <img
                      src="/assets/carelonRx/images/infoIconRed.svg"
                      alt=""
                      className="mfa-info-icon"
                    />
                    <div className="mfa-disclaimer-text">
                      If your contact details are incorrect, please call the Pharmacy Member
                      Services number on your member ID card. If you don't have your ID card,
                      please check back later. We are currently experiencing an issue displaying
                      the number and are working to fix it. Pharmacy Member Services is available 24/7.
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer with Buttons */}
        <div className="mfa-footer">
          <div className="mfa-footer-buttons">
            <button onClick={() => setShowCancelModal(true)} className="crx-button crx-button-secondary">
              Cancel
            </button>
            <button 
              onClick={error ? undefined : handleConfirm} 
              disabled={!!error}
              className="crx-button crx-button-primary"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <CancelMFAModal
        show={showCancelModal}
        onDismiss={() => setShowCancelModal(false)}
        onConfirm={onBack}
      />
    </PageLayout>
  );
};

export default ReviewDetailsPage;
